import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Loader2, Building2, User } from 'lucide-react';
import { toast } from 'sonner';

const SSOComplete = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isTeam, setIsTeam] = useState(false);
  const [orgName, setOrgName] = useState('');

  // SSO data from URL params (for Microsoft) or sessionStorage (for Google)
  const [ssoData, setSsoData] = useState(null);

  useEffect(() => {
    const provider = searchParams.get('sso');
    if (provider) {
      // From Microsoft redirect
      setSsoData({
        email: searchParams.get('email'),
        name: searchParams.get('name'),
        provider,
        ssoId: searchParams.get('ssoId'),
        picture: searchParams.get('picture') || null
      });
    } else {
      // From Google (stored in sessionStorage)
      const stored = sessionStorage.getItem('sso_signup_data');
      if (stored) {
        setSsoData(JSON.parse(stored));
      } else {
        navigate('/register');
      }
    }
  }, [searchParams, navigate]);

  const handleComplete = async () => {
    if (isTeam && !orgName.trim()) {
      toast.error('Organization name is required');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/complete-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: ssoData.email,
          name: ssoData.name,
          provider: ssoData.provider,
          ssoId: ssoData.ssoId,
          picture: ssoData.picture,
          organizationName: isTeam ? orgName.trim() : null
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Signup failed');

      sessionStorage.removeItem('sso_signup_data');
      loginWithToken(data.token, data.user);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (err) {
      toast.error(err.message || 'Failed to complete registration');
    } finally {
      setLoading(false);
    }
  };

  if (!ssoData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6">
      <Card className="w-full max-w-md border-0 shadow-xl bg-white dark:bg-slate-900" data-testid="sso-complete-card">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-2xl font-bold text-slate-900 dark:text-white">Complete Your Setup</CardTitle>
          <CardDescription className="text-slate-500 dark:text-slate-400">
            Almost there! Just a few more details.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
            {ssoData.picture ? (
              <img src={ssoData.picture} alt="" className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            )}
            <div>
              <p className="font-medium text-slate-900 dark:text-white">{ssoData.name}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{ssoData.email}</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900 dark:text-white text-sm">Setting up for a team?</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Enable if you'll manage a team</p>
              </div>
            </div>
            <Switch checked={isTeam} onCheckedChange={setIsTeam} data-testid="sso-team-toggle" />
          </div>

          {isTeam && (
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Organization Name</Label>
              <Input
                placeholder="e.g. Bank of America"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="dark:bg-slate-800 dark:border-slate-700"
                data-testid="sso-org-name"
              />
            </div>
          )}

          <Button
            onClick={handleComplete}
            disabled={loading}
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl"
            data-testid="sso-complete-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {isTeam ? 'Create Team Account' : 'Start with Individual Plan'}
          </Button>

          <p className="text-xs text-center text-slate-400 dark:text-slate-500">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SSOComplete;
